// ===================================================================
// Time & Labor — Bulk CSV Import
// Upload CSV to create multiple labor records at once
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Table, Button, Group, Badge, Stack,
  LoadingOverlay, Stepper, FileInput,
} from '../../components/gab/GabComponents';
import {
  IconUpload, IconDownload, IconCheck, IconX, IconFileSpreadsheet,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useTimeLaborStore } from '../../store/timeLaborStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';

const TEAL = '#0D9488';

function pf(v: string | number | undefined): number {
  if (typeof v === 'number') return v || 0;
  return parseFloat(stripCurrency(v || '')) || 0;
}

const CSV_HEADERS = [
  'Employee Name', 'Employee Title', 'Employee ID', 'Work Date',
  'Regular Hours', 'Overtime Hours', 'Regular Rate', 'Overtime Rate',
  'Fringe %', 'Activity', 'Category', 'Site/Location', 'Notes',
];

const EXAMPLE_ROWS = [
  ['Jane Smith', 'Heavy Equipment Operator', 'EMP-042', '2026-04-10', '8', '2', '28.50', '42.75', '35.2', 'Debris Removal', 'A', 'Main St & 5th Ave', 'Storm cleanup'],
  ['John Doe', 'Electrician', 'EMP-017', '2026-04-10', '8', '0', '32.00', '48.00', '38.5', 'Utility Restoration', 'F', 'Riverside Substation', ''],
  ['Maria Garcia', 'Admin Assistant', 'EMP-103', '2026-04-10', '8', '0', '22.00', '33.00', '30.1', 'EOC Operations', 'B', 'County EOC', 'Night shift coverage'],
];

interface ParsedRow {
  rowNum: number;
  employeeName: string;
  employeeTitle: string;
  employeeId: string;
  workDate: string;
  regHours: number;
  otHours: number;
  regRate: number;
  otRate: number;
  fringePct: number;
  activity: string;
  category: string;
  site: string;
  notes: string;
  valid: boolean;
  errors: string[];
  autoFilled: boolean;
}

function parseCSV(text: string): string[][] {
  const lines = text.split('\n').filter(l => l.trim());
  return lines.map(line => {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') { inQuotes = !inQuotes; continue; }
      if (char === ',' && !inQuotes) { cells.push(current.trim()); current = ''; continue; }
      current += char;
    }
    cells.push(current.trim());
    return cells;
  });
}

function isValidDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(s + 'T12:00:00');
  return !isNaN(d.getTime());
}

export default function BulkImport() {
  const { canEdit } = useModulePermission('timelabor');
  const activeIncident = useAuthStore(s => s.activeIncident);

  const { employees, fetchEmployees, createLaborRecord } = useTimeLaborStore();
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);

  useEffect(() => {
    fetchEmployees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build name -> employee profile map (case-insensitive)
  const profileByName = useMemo(() => {
    const m = new Map<string, typeof employees[0]>();
    employees.forEach(e => {
      if (e.employeeName) m.set(e.employeeName.toLowerCase(), e);
    });
    return m;
  }, [employees]);

  // Download template
  const handleDownloadTemplate = () => {
    const headerLine = CSV_HEADERS.join(',');
    const exampleLines = EXAMPLE_ROWS.map(row =>
      row.map(cell => cell.includes(',') ? `"${cell}"` : cell).join(',')
    );
    const csv = [headerLine, ...exampleLines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'time_labor_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Parse uploaded file
  const handleFileUpload = async (f: File | null) => {
    setFile(f);
    if (!f) { setParsedRows([]); return; }

    const text = await f.text();
    const rawRows = parseCSV(text);

    if (rawRows.length < 2) {
      notifications.show({ title: 'Invalid CSV', message: 'File must have a header row and at least one data row', color: 'red' });
      return;
    }

    // Skip header row
    const dataRows = rawRows.slice(1);
    const parsed: ParsedRow[] = dataRows.map((cells, idx) => {
      const errors: string[] = [];
      const name = cells[0] || '';
      const title = cells[1] || '';
      const empId = cells[2] || '';
      const dateStr = cells[3] || '';
      const regHrs = parseFloat(cells[4] || '0') || 0;
      const otHrs = parseFloat(cells[5] || '0') || 0;
      let regRate = parseFloat(cells[6] || '0') || 0;
      let otRate = parseFloat(cells[7] || '0') || 0;
      let fringePct = parseFloat(cells[8] || '0') || 0;
      const activity = cells[9] || '';
      const category = cells[10] || '';
      const site = cells[11] || '';
      const notes = cells[12] || '';

      // Validation
      if (!name) errors.push('Employee Name required');
      if (!dateStr) errors.push('Work Date required');
      else if (!isValidDate(dateStr)) errors.push('Invalid date format (use YYYY-MM-DD)');
      if (regHrs <= 0 && otHrs <= 0) errors.push('Must have Regular Hours or Overtime Hours > 0');

      // Auto-fill from profile if name matches
      let autoFilled = false;
      const profile = name ? profileByName.get(name.toLowerCase()) : undefined;
      if (profile) {
        if (regRate === 0) { regRate = pf(profile.regularRate); autoFilled = true; }
        if (otRate === 0) { otRate = pf(profile.overtimeRate); autoFilled = true; }
        if (fringePct === 0) { fringePct = pf(profile.totalFringePct); autoFilled = true; }
      }

      return {
        rowNum: idx + 2,
        employeeName: name,
        employeeTitle: title || (profile?.jobTitle || ''),
        employeeId: empId || (profile?.employeeId || ''),
        workDate: dateStr,
        regHours: regHrs,
        otHours: otHrs,
        regRate,
        otRate,
        fringePct,
        activity,
        category,
        site,
        notes,
        valid: errors.length === 0,
        errors,
        autoFilled,
      };
    });

    setParsedRows(parsed);
    setActiveStep(1);
  };

  const validRows = parsedRows.filter(r => r.valid);
  const invalidRows = parsedRows.filter(r => !r.valid);

  // Import
  const handleImport = async () => {
    if (validRows.length === 0) return;
    setImporting(true);
    let success = 0;
    let failed = 0;

    for (const row of validRows) {
      try {
        const laborCost = (row.regHours * row.regRate) + (row.otHours * row.otRate);
        const fringeCost = laborCost * row.fringePct / 100;

        // Look up profile for fringe breakdown
        const profile = profileByName.get(row.employeeName.toLowerCase());
        const ficaPct = profile ? pf(profile.fringeFicaPct) : 0;
        const retPct = profile ? pf(profile.fringeRetirementPct) : 0;
        const healthPct = profile ? pf(profile.fringeHealthPct) : 0;
        const wcPct = profile ? pf(profile.fringeWorkersCompPct) : 0;
        const uiPct = profile ? pf(profile.fringeUnemploymentPct) : 0;
        const otherPct = profile ? pf(profile.fringeOtherPct) : 0;

        await createLaborRecord({
          projectId: '',
          organizationId: profile?.organizationId || '',
          employeeName: row.employeeName,
          employeeTitle: row.employeeTitle,
          employeeId: row.employeeId,
          workDate: row.workDate,
          hoursRegular: row.regHours.toString(),
          hoursOvertime: row.otHours.toString(),
          rateRegular: row.regRate.toFixed(2),
          rateOvertime: row.otRate.toFixed(2),
          fringeBenefitsPct: row.fringePct.toFixed(1),
          fringeFica: (laborCost * ficaPct / 100).toFixed(2),
          fringeRetirement: (laborCost * retPct / 100).toFixed(2),
          fringeHealth: (laborCost * healthPct / 100).toFixed(2),
          fringeWorkersComp: (laborCost * wcPct / 100).toFixed(2),
          fringeUnemployment: (laborCost * uiPct / 100).toFixed(2),
          fringeOther: (laborCost * otherPct / 100).toFixed(2),
          laborCost: laborCost.toFixed(2),
          fringeCost: fringeCost.toFixed(2),
          totalCost: (laborCost + fringeCost).toFixed(2),
          activityDescription: row.activity,
          category: row.category,
          timesheetOnFile: 'draft',
          notes: row.site ? (row.notes ? `${row.site} | ${row.notes}` : row.site) : row.notes,
          incidentId: activeIncident?.id ? String(activeIncident.id) : '',
        });
        success++;
      } catch {
        failed++;
      }
    }

    setImportResult({ success, failed });
    setActiveStep(2);
    setImporting(false);
    notifications.show({
      title: 'Import Complete',
      message: `${success} entries imported${failed > 0 ? `, ${failed} failed` : ''}`,
      color: failed > 0 ? 'orange' : 'green',
    });
  };

  const resetForm = () => {
    setActiveStep(0);
    setFile(null);
    setParsedRows([]);
    setImportResult(null);
  };

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={importing} />

      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Bulk Import</Title>
          <Text size="sm" c="dimmed">Upload a CSV file to create multiple time entries at once</Text>
        </div>
      </Group>

      <Stepper active={activeStep} mb="xl" color={TEAL}>
        <Stepper.Step label="Upload" description="Download template & upload CSV" />
        <Stepper.Step label="Review" description="Validate & preview" />
        <Stepper.Step label="Done" description="Import complete" />
      </Stepper>

      {/* Step 1: Template & Upload */}
      {activeStep === 0 && (
        <Stack gap="lg">
          <Paper p="md" withBorder style={{ borderTop: `3px solid ${TEAL}` }}>
            <Text fw={600} mb="sm">Step 1: Download Template</Text>
            <Text size="sm" c="dimmed" mb="md">
              Download the CSV template, fill in your time entries, and upload the completed file.
            </Text>
            <Button variant="light" color={TEAL} leftSection={<IconDownload size={14} />}
              onClick={handleDownloadTemplate}>
              Download CSV Template
            </Button>
          </Paper>

          <Paper p="md" withBorder>
            <Text fw={600} mb="sm">Column Reference</Text>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Column</Table.Th>
                  <Table.Th>Required</Table.Th>
                  <Table.Th>Description</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr><Table.Td fw={500}>Employee Name</Table.Td><Table.Td><Badge color="red" size="xs">Required</Badge></Table.Td><Table.Td>Full name (auto-fills rate/fringe if matches a labor profile)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Employee Title</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>Job title (auto-filled from profile if blank)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Employee ID</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>Employee identifier (auto-filled from profile if blank)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Work Date</Table.Td><Table.Td><Badge color="red" size="xs">Required</Badge></Table.Td><Table.Td>Date in YYYY-MM-DD format</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Regular Hours</Table.Td><Table.Td><Badge color="red" size="xs">Required*</Badge></Table.Td><Table.Td>Regular hours worked (at least one of Reg/OT must be &gt; 0)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Overtime Hours</Table.Td><Table.Td><Badge color="red" size="xs">Required*</Badge></Table.Td><Table.Td>Overtime hours worked</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Regular Rate</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>Hourly rate (auto-filled from profile if 0 or blank)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Overtime Rate</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>OT hourly rate (auto-filled from profile if 0 or blank)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Fringe %</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>Fringe benefit percentage (auto-filled from profile if 0 or blank)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Activity</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>Task description (e.g., Debris Removal, EOC Operations)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Category</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>FEMA damage category (A-G or Z)</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Site/Location</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>Work site or location name</Table.Td></Table.Tr>
                <Table.Tr><Table.Td fw={500}>Notes</Table.Td><Table.Td><Badge color="gray" size="xs">Optional</Badge></Table.Td><Table.Td>Additional notes</Table.Td></Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>

          <Paper p="md" withBorder style={{ borderTop: `3px solid ${TEAL}` }}>
            <Text fw={600} mb="sm">Step 2: Upload CSV</Text>
            <FileInput
              accept=".csv"
              placeholder="Choose CSV file..."
              leftSection={<IconUpload size={14} />}
              value={file}
              onChange={handleFileUpload}
              style={{ maxWidth: 400 }}
            />
          </Paper>
        </Stack>
      )}

      {/* Step 2: Review */}
      {activeStep === 1 && (
        <Stack gap="lg">
          {/* Summary */}
          <Group gap={12}>
            <Badge size="lg" variant="light" color="green" leftSection={<IconCheck size={12} />}>
              {validRows.length} valid
            </Badge>
            {invalidRows.length > 0 && (
              <Badge size="lg" variant="light" color="red" leftSection={<IconX size={12} />}>
                {invalidRows.length} errors
              </Badge>
            )}
            <Badge size="lg" variant="light" color={TEAL}>
              {validRows.reduce((s, r) => s + r.regHours + r.otHours, 0).toFixed(1)} total hours
            </Badge>
            <Badge size="lg" variant="light" color="green">
              {formatCurrency(validRows.reduce((s, r) => {
                const lc = (r.regHours * r.regRate) + (r.otHours * r.otRate);
                return s + lc + (lc * r.fringePct / 100);
              }, 0).toFixed(2))} total cost
            </Badge>
          </Group>

          {/* Auto-fill info */}
          {parsedRows.some(r => r.autoFilled) && (
            <Paper p="xs" withBorder bg="blue.0">
              <Text size="sm" c="blue.8">
                <IconFileSpreadsheet size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Some rows had rates/fringe auto-filled from matching employee profiles.
              </Text>
            </Paper>
          )}

          {/* Preview table */}
          <Paper withBorder style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Row</Table.Th>
                  <Table.Th>Employee</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Reg Hrs</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>OT Hrs</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Rate</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Fringe %</Table.Th>
                  <Table.Th>Activity</Table.Th>
                  <Table.Th>Cat</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Total $</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {parsedRows.map(r => {
                  const laborCost = (r.regHours * r.regRate) + (r.otHours * r.otRate);
                  const totalCost = laborCost + (laborCost * r.fringePct / 100);
                  return (
                    <Table.Tr key={r.rowNum} style={r.valid ? undefined : { background: 'var(--mantine-color-red-0)' }}>
                      <Table.Td>{r.rowNum}</Table.Td>
                      <Table.Td fw={500}>
                        {r.employeeName || '-'}
                        {r.autoFilled && <Badge size="xs" ml={4} variant="light" color="blue">auto-filled</Badge>}
                      </Table.Td>
                      <Table.Td>{r.workDate || '-'}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{r.regHours.toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{r.otHours > 0 ? r.otHours.toFixed(1) : '-'}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{r.regRate > 0 ? `$${r.regRate.toFixed(2)}` : '-'}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{r.fringePct > 0 ? `${r.fringePct.toFixed(1)}%` : '-'}</Table.Td>
                      <Table.Td><Text size="sm" truncate style={{ maxWidth: 150 }}>{r.activity || '-'}</Text></Table.Td>
                      <Table.Td>{r.category || '-'}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }} fw={600}>{r.valid ? formatCurrency(totalCost.toFixed(2)) : '-'}</Table.Td>
                      <Table.Td>
                        {r.valid ? (
                          <Badge size="sm" variant="light" color="green">Valid</Badge>
                        ) : (
                          <Badge size="sm" variant="light" color="red">{r.errors.join('; ')}</Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>

          {/* Actions */}
          <Group justify="flex-end">
            <Button variant="light" color="gray" onClick={resetForm}>Back</Button>
            {canEdit && (
              <Button color={TEAL} leftSection={<IconUpload size={14} />}
                onClick={handleImport} loading={importing}
                disabled={validRows.length === 0}>
                Import {validRows.length} Valid Entries
              </Button>
            )}
          </Group>
        </Stack>
      )}

      {/* Step 3: Done */}
      {activeStep === 2 && importResult && (
        <Paper p="xl" withBorder ta="center">
          <IconCheck size={48} color="#059669" style={{ marginBottom: 16 }} />
          <Title order={4} mb="sm">Import Complete</Title>
          <Text size="lg" mb="md">
            <strong>{importResult.success}</strong> time entries imported as <Badge variant="light" color="gray">draft</Badge> status.
            {importResult.failed > 0 && (
              <> <strong style={{ color: '#DC2626' }}>{importResult.failed}</strong> entries failed.</>
            )}
          </Text>
          <Text size="sm" c="dimmed" mb="lg">
            Imported entries are in draft status. Employees can submit them for supervisor review from Time Entry.
          </Text>
          <Group justify="center">
            <Button variant="light" color={TEAL} onClick={resetForm}>Import More</Button>
            <Button color={TEAL} onClick={() => window.location.href = '/timelabor/timeentry'}>
              View Time Entries
            </Button>
          </Group>
        </Paper>
      )}
    </div>
  );
}
